import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

// Helper to safely parse JSON
const safeParseJSON = (str, defaultValue = {}) => {
  try {
    return JSON.parse(str || '{}');
  } catch {
    return defaultValue;
  }
};

// Helper to update JSON field
const updateJSONField = (currentValue, field, newValue, onChange) => {
  const data = safeParseJSON(currentValue);
  data[field] = newValue;
  onChange(JSON.stringify(data));
};

export function SuspectedLoverForm({ value, onChange, language }) {
  const data = safeParseJSON(value);
  const it = language === 'it';
  
  const handleFieldChange = (field) => (e) => {
    updateJSONField(value, field, e.target.value, onChange);
  };

  const loverField = (label, field, placeholder, upper = false) => (
    <div>
      <label className="font-mono text-xs text-gray-400 mb-1 block">{label}</label>
      <Input
        type="text"
        value={data[field] || ''}
        onChange={(e) => updateJSONField(value, field, upper ? e.target.value.toUpperCase() : e.target.value, onChange)}
        placeholder={placeholder}
        data-testid={`lover-${field}`}
        className={`cyber-input w-full h-11 rounded-md px-3 text-sm ${upper ? 'uppercase tracking-wider' : ''}`}
      />
    </div>
  );
  
  return (
    <div className="space-y-4">
      <div className="font-mono text-xs text-neon-cyan/80 mb-2">
        {language === 'it' 
          ? '💡 Inserisci tutte le informazioni disponibili (campi opzionali; se indichi dove abita, la città è obbligatoria):' 
          : '💡 Enter all available information (optional fields; if you enter where they live, the city is required):'}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="font-mono text-xs text-gray-400 mb-1 block">
            {language === 'it' ? 'Nome/Soprannome' : 'Name/Nickname'}
          </label>
          <Input
            type="text"
            value={data.nome || ''}
            onChange={handleFieldChange('nome')}
            placeholder={language === 'it' ? 'Es: Marco, "Il biondo"...' : 'Ex: Mark, "The blonde"...'}
            className="cyber-input w-full h-11 rounded-md px-3 text-sm"
          />
        </div>
        
        {loverField(it ? 'Dove abita (via/zona)' : 'Where they live (street/area)', 'indirizzo', it ? 'Indirizzo o zona...' : 'Address or area...')}
        {loverField(data.indirizzo?.trim() ? (it ? 'Città *' : 'City *') : (it ? 'Città' : 'City'), 'citta', it ? 'Es: Milano' : 'Ex: Milan')}
        
        <div>
          <label className="font-mono text-xs text-gray-400 mb-1 block">
            {language === 'it' ? 'Dove lavora' : 'Where they work'}
          </label>
          <Input
            type="text"
            value={data.lavoro || ''}
            onChange={handleFieldChange('lavoro')}
            placeholder={language === 'it' ? 'Azienda, ruolo...' : 'Company, role...'}
            className="cyber-input w-full h-11 rounded-md px-3 text-sm"
          />
        </div>
        
        {loverField(it ? 'Targa veicolo' : 'Vehicle plate', 'targa', 'AB123CD', true)}
        {loverField(it ? 'Marca veicolo (opzionale)' : 'Vehicle make (optional)', 'marca', it ? 'Es: BMW' : 'Ex: BMW')}
        {loverField(it ? 'Modello veicolo (opzionale)' : 'Vehicle model (optional)', 'modello', it ? 'Es: Serie 1' : 'Ex: 1 Series')}
        {loverField(it ? 'Colore veicolo (opzionale)' : 'Vehicle color (optional)', 'colore', it ? 'Es: Nero' : 'Ex: Black')}
        
        <div>
          <label className="font-mono text-xs text-gray-400 mb-1 block">
            {language === 'it' ? 'Come si sono conosciuti' : 'How they met'}
          </label>
          <Input
            type="text"
            value={data.conosciuti || ''}
            onChange={handleFieldChange('conosciuti')}
            placeholder={language === 'it' ? 'Lavoro, palestra, social...' : 'Work, gym, social media...'}
            className="cyber-input w-full h-11 rounded-md px-3 text-sm"
          />
        </div>
        
        <div>
          <label className="font-mono text-xs text-gray-400 mb-1 block">
            {language === 'it' ? 'Descrizione fisica' : 'Physical description'}
          </label>
          <Input
            type="text"
            value={data.descrizione || ''}
            onChange={handleFieldChange('descrizione')}
            placeholder={language === 'it' ? 'Altezza, capelli, segni...' : 'Height, hair, marks...'}
            className="cyber-input w-full h-11 rounded-md px-3 text-sm"
          />
        </div>
      </div>
      
      <div>
        <label className="font-mono text-xs text-gray-400 mb-1 block">
          {language === 'it' ? 'Altre informazioni utili' : 'Other useful information'}
        </label>
        <Textarea
          value={data.altro || ''}
          onChange={handleFieldChange('altro')}
          placeholder={language === 'it' 
            ? 'Profili social, frequentazioni, relazione con il soggetto...' 
            : 'Social profiles, hangouts, relationship with subject...'}
          data-testid="answer-input"
          className="cyber-input w-full min-h-[80px] resize-none rounded-md p-3 text-sm"
        />
      </div>
    </div>
  );
}

export function SocialProfilesForm({ value, onChange, language }) {
  const data = safeParseJSON(value);
  
  const handleFieldChange = (field) => (e) => {
    updateJSONField(value, field, e.target.value, onChange);
  };
  
  return (
    <div className="space-y-4">
      <div className="font-mono text-xs text-neon-cyan/80 mb-2">
        {language === 'it' 
          ? '💡 Inserisci i profili social conosciuti (tutti i campi sono opzionali):' 
          : '💡 Enter known social profiles (all fields are optional):'}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="font-mono text-xs text-gray-400 mb-1 block">Facebook</label>
          <Input
            type="text"
            value={data.facebook || ''}
            onChange={handleFieldChange('facebook')}
            placeholder={language === 'it' ? 'Nome profilo o URL...' : 'Profile name or URL...'}
            className="cyber-input w-full h-11 rounded-md px-3 text-sm"
          />
        </div>
        
        <div>
          <label className="font-mono text-xs text-gray-400 mb-1 block">Instagram</label>
          <Input
            type="text"
            value={data.instagram || ''}
            onChange={handleFieldChange('instagram')}
            placeholder="@username"
            className="cyber-input w-full h-11 rounded-md px-3 text-sm"
          />
        </div>
        
        <div>
          <label className="font-mono text-xs text-gray-400 mb-1 block">TikTok</label>
          <Input
            type="text"
            value={data.tiktok || ''}
            onChange={handleFieldChange('tiktok')}
            placeholder="@username"
            className="cyber-input w-full h-11 rounded-md px-3 text-sm"
          />
        </div>
        
        <div>
          <label className="font-mono text-xs text-gray-400 mb-1 block">LinkedIn</label>
          <Input
            type="text"
            value={data.linkedin || ''}
            onChange={handleFieldChange('linkedin')}
            placeholder={language === 'it' ? 'Nome o URL profilo...' : 'Name or profile URL...'}
            className="cyber-input w-full h-11 rounded-md px-3 text-sm"
          />
        </div>
      </div>
      
      <div>
        <label className="font-mono text-xs text-gray-400 mb-1 block">
          {language === 'it' ? 'Altri social o app di dating' : 'Other social or dating apps'}
        </label>
        <Textarea
          value={data.altri || ''}
          onChange={handleFieldChange('altri')}
          placeholder={language === 'it' 
            ? 'Es: Twitter, Tinder, Badoo, WhatsApp (numero)...' 
            : 'Ex: Twitter, Tinder, Badoo, WhatsApp (number)...'}
          data-testid="answer-input"
          className="cyber-input w-full min-h-[60px] resize-none rounded-md p-3 text-sm"
        />
      </div>
    </div>
  );
}
